import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography
} from "antd";
import {
  ClearOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined
} from "@ant-design/icons";
import { ToastContainer, toast } from "react-toastify";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import axios from "axios";
import { getHeaders } from "../../utility/getHeader";
import fetchData from "../../functions/fetchData";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "react-toastify/dist/ReactToastify.css";
import "../master/ExpenseHead.css";
import "./StockReportNew.css";
import logo from "../../assets/logo.png";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const REPORT_TYPE_OPTIONS = [
  { value: "all", label: "All Records" },
  { value: "item", label: "By Item" },
  { value: "supplier", label: "By Supplier" },
  { value: "category", label: "By Category" },
  { value: "reference", label: "By Reference" },
  { value: "groupbyinv", label: "Group by Inv" },
  { value: "transactions", label: "Transactions" },
  { value: "balance", label: "Balance" }
];

const getNumericValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === "") {
      continue;
    }

    const parsedValue = Number(value);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
};

const getTextValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) {
      continue;
    }

    const trimmedValue = String(value).trim();
    if (trimmedValue) {
      return trimmedValue;
    }
  }

  return "";
};

export default function StockReportNew() {
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    items: [],
    suppliers: [],
    sponsors: [],
    references: [],
    date_range: { earliest_date: null, latest_date: null },
    stock_types: []
  });

  const [filters, setFilters] = useState({
    from: "",
    to: "",
    itemid: "",
    name: "",
    catid: "",
    sname: "",
    sponsor_name: "",
    ref: "",
    page: 1,
    limit: 50
  });

  const [reportData, setReportData] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    limit: 50,
    total_records: 0,
    total_pages: 0
  });
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  const displayData = useMemo(() => {
    if (filterType !== "item") {
      return reportData;
    }

    const sortedRows = [...reportData].sort((left, right) => {
      const leftDate = new Date(left.date || 0).getTime();
      const rightDate = new Date(right.date || 0).getTime();

      if (leftDate !== rightDate) {
        return leftDate - rightDate;
      }

      const leftPo = Number(left.po) || 0;
      const rightPo = Number(right.po) || 0;

      if (leftPo !== rightPo) {
        return leftPo - rightPo;
      }

      return (Number(left.id) || 0) - (Number(right.id) || 0);
    });

    let previousClosingStock = null;

    const normalizedRows = sortedRows.map((row) => {
      const stockIn = getNumericValue(row.qty, row.receipt_qty, row.in_qty, 0) ?? 0;
      const stockOut = getNumericValue(row.sold_qty, row.out_qty, row.issue_qty, 0) ?? 0;
      const rawOpeningStock = getNumericValue(row.ostock, row.opening_stock);
      const openingStock = previousClosingStock ?? rawOpeningStock ?? 0;
      const closingStock = getNumericValue(
        row.total,
        row.closing_stock,
        row.balance,
        openingStock + stockIn - stockOut
      ) ?? 0;

      previousClosingStock = closingStock;

      return {
        ...row,
        qty: stockIn,
        sold_qty: stockOut,
        ref: getTextValue(row.ref, row.reference, row.inv),
        derived_ostock: openingStock,
        derived_closing_stock: closingStock
      };
    });

    return normalizedRows.sort((left, right) => {
      const leftDate = new Date(left.date || 0).getTime();
      const rightDate = new Date(right.date || 0).getTime();

      if (leftDate !== rightDate) {
        return rightDate - leftDate;
      }

      const leftPo = Number(left.po) || 0;
      const rightPo = Number(right.po) || 0;

      if (leftPo !== rightPo) {
        return rightPo - leftPo;
      }

      return (Number(right.id) || 0) - (Number(left.id) || 0);
    });
  }, [filterType, reportData]);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Load filter options on mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoading(true);

        // Fetch stock data directly from wstock table
        await fetchData("wstock", (stockData) => {
          // Extract unique categories
          const uniqueCategories = [...new Set(stockData.map((item) => item.category || item.type).filter(Boolean))]
            .sort()
            .map((cat) => ({ value: cat, label: cat }));

          // Extract unique suppliers
          const uniqueSuppliers = [...new Set(stockData.map((item) => item.sname).filter(Boolean))]
            .sort()
            .map((sup) => ({ value: sup, label: sup }));

          // Extract unique sponsors
          const uniqueSponsors = [...new Set(stockData.map((item) => item.sponsor_name).filter(Boolean))]
            .sort()
            .map((spon) => ({ value: spon, label: spon }));

          // Extract unique references
          const uniqueReferences = [...new Set(stockData.map((item) => item.ref).filter(Boolean))].sort();

          // Get date range
          const dates = stockData.map((item) => item.date).filter(Boolean).sort();
          const nextDateRange = {
            earliest_date: dates[0] || null,
            latest_date: dates[dates.length - 1] || null
          };

          // Fetch items from items table
          fetchData(
            "items",
            (itemsData) => {
            // Deduplicate by item name
            const uniqueByName = new Map();
            itemsData.forEach((item) => {
              const key = (item.name || "").trim();
              if (key && !uniqueByName.has(key)) {
                uniqueByName.set(key, { name: item.name, label: item.name });
              }
            });
            const items = Array.from(uniqueByName.values()).sort((a, b) => a.label.localeCompare(b.label));

            setFilterOptions({
              categories: uniqueCategories,
              items: items,
              suppliers: uniqueSuppliers,
              sponsors: uniqueSponsors,
              references: uniqueReferences,
              date_range: nextDateRange,
              stock_types: [
                { value: "in", label: "Stock In" },
                { value: "out", label: "Stock Out" }
              ]
            });

            setLoading(false);
          },
            "name",
            { flag: 1 }
          );
        },
        "date",
        {}
        );
      } catch (error) {
        console.error("Error loading filter options:", error);
        toast.error("Failed to load filter options");
        setLoading(false);
      }
    };
    loadFilterOptions();
  }, []);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  // Handle date range changes
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    if (dates && dates[0] && dates[1]) {
      setFilters((prev) => ({
        ...prev,
        from: dates[0].format("YYYY-MM-DD"),
        to: dates[1].format("YYYY-MM-DD"),
        page: 1
      }));
    } else {
      setFilters((prev) => ({
        ...prev,
        from: "",
        to: "",
        page: 1
      }));
    }
  };

  const handleSelectFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value || "",
      page: 1
    }));
  };

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
    setFilters((prev) => ({
      ...prev,
      name: "",
      sname: "",
      catid: "",
      ref: "",
      page: 1
    }));
    setReportData([]);
  };

  // Load report data
  const loadReport = async () => {
    if (!filters.from || !filters.to) {
      toast.warn("Please select date range");
      return;
    }

    try {
      setLoading(true);
      let response;

      const params = {
        from: filters.from,
        to: filters.to
      };

      // Route selection based on filter type
      if (filterType === "item" && filters.name) {
        // Filter by date and item
        params.item = filters.name;
        response = await axios.get("/stock/filter-by-date-item", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "supplier" && filters.sname) {
        // Filter by date and supplier
        params.supplier = filters.sname;
        response = await axios.get("/stock/filter-by-date-supplier", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "category" && filters.catid) {
        // Filter by date then filter category client-side
        response = await axios.get("/stock/filter-by-date", {
          ...getHeaders(),
          params 
        });
        // Filter by category on client side
        if (response.data && response.data.data) {
          response.data.data = response.data.data.filter(
            (item) => item.category === filters.catid
          );
        }
      } else if (filterType === "reference") {
        // Group by invoice/reference
        response = await axios.get("/stock/group-by-invoice", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "groupbyinv") {
        // Group by invoice with date range
        params.groupBy = "inv";
        response = await axios.get("/stock/group-by-invoice", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "transactions") {
        // Filter by date - all transactions
        response = await axios.get("/stock/filter-by-date", {
          ...getHeaders(),
          params 
        });
      } else if (filterType === "balance") {
        // Group by item for balance view
        response = await axios.get("/stock/filter-group-by-item", {
          ...getHeaders(),
          params 
        });
      } else {
        // Default: Filter by date only
        response = await axios.get("/stock/filter-by-date", {
          ...getHeaders(),
          params 
        });
      }

      if (response.data) {
        // Check if data is directly in response.data or nested in response.data.data
        const dataArray = Array.isArray(response.data)
          ? response.data
          : (response.data.data || []);

        setReportData(dataArray);
        setPagination({
          current_page: response.data.pagination?.current_page || 1,
          limit: response.data.pagination?.limit || dataArray.length || 50,
          total_records: response.data.pagination?.total_records || dataArray.length,
          total_pages: response.data.pagination?.total_pages || 1
        });
        toast.success(response.data.msg || `Loaded ${dataArray.length} records`);
      } else {
        toast.error("Failed to load report");
      }
    } catch (error) {
      console.error("Error loading report:", error);
      toast.error(error.response?.data?.msg || "Error loading report");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const resetFilters = () => {
    setFilters({
      from: "",
      to: "",
      itemid: "",
      name: "",
      catid: "",
      sname: "",
      sponsor_name: "",
      ref: "",
      page: 1,
      limit: 50
    });
    setFilterType("all");
    setDateRange(null);
    setReportData([]);
    setPagination({
      current_page: 1,
      limit: 50,
      total_records: 0,
      total_pages: 0
    });
  };

  const getColumns = () => {
    if (filterType === "item") {
      return [
        { key: "po", label: "PO" },
        { key: "date", label: "Date", isDate: true },
        { key: "ref", label: "Ref" },
        { key: "sname", label: "Supplier" },
        { key: "name", label: "Item" },
        { key: "pass", label: "Pass No." },
        { key: "ostock", label: "Opening Stock", isNumeric: true },
        { key: "qty", label: "In", isNumeric: true },
        { key: "sold_qty", label: "Out", isNumeric: true },
        { key: "total", label: "Closing Stock", isNumeric: true },
        { key: "rate", label: "Rate", isCurrency: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    } else if (filterType === "supplier") {
      return [
        { key: "sno", label: "S.No." },
        { key: "inv", label: "Inv. No" },
        { key: "name", label: "Item Name" },
        { key: "qty", label: "Inward", isNumeric: true },
        { key: "rate", label: "Unit Price", isCurrency: true },
        { key: "amount", label: "Total Amount", isCurrency: true }
      ];
    } else if (filterType === "transactions") {
      return [
        { key: "date", label: "Date", isDate: true },
        { key: "name", label: "Item" },
        { key: "category", label: "Category" },
        { key: "qty", label: "Qty", isNumeric: true },
        { key: "rate", label: "Rate", isCurrency: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    } else if (filterType === "balance") {
      return [
        { key: "name", label: "Item Name" },
        { key: "opening_balance", label: "Opening", isNumeric: true },
        { key: "period_inward", label: "Inward", isNumeric: true },
        { key: "period_outward", label: "Outward", isNumeric: true },
        { key: "closing_balance", label: "Closing", isNumeric: true }
      ];
    } else if (filterType === "reference") {
      return [
        { key: "reference", label: "Reference" },
        { key: "supplier_name", label: "Supplier" },
        { key: "sponsor_name", label: "Sponsor" },
        { key: "unique_items", label: "Items", isNumeric: true },
        { key: "total_inward", label: "Inward Qty", isNumeric: true },
        { key: "total_outward", label: "Outward Qty", isNumeric: true }
      ];
    } else if (filterType === "groupbyinv") {
      return [
        { key: "sno", label: "S.No." },
        { key: "supplier", label: "Supplier Name" },
        { key: "inv", label: "Inv No." },
        { key: "date", label: "Date", isDate: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    } else if (filterType === "category") {
      return [
        { key: "date", label: "Date", isDate: true },
        { key: "name", label: "Item" },
        { key: "category", label: "Category" },
        { key: "sname", label: "Supplier" },
        { key: "qty", label: "Qty", isNumeric: true },
        { key: "rate", label: "Rate", isCurrency: true },
        { key: "amount", label: "Amount", isCurrency: true }
      ];
    }

    return [
      { key: "date", label: "Date", isDate: true },
      { key: "name", label: "Item" },
      { key: "sname", label: "Supplier" },
      { key: "category", label: "Category" },
      { key: "qty", label: "Qty", isNumeric: true },
      { key: "sold", label: "Sold Qty", isNumeric: true },
      { key: "rate", label: "Rate", isCurrency: true },
      { key: "amount", label: "Amount", isCurrency: true }
    ];
  };

  const columns = getColumns();

  const calculateTotals = () => {
    const totals = {};

    columns.forEach(col => {
      if (["qty", "sold_qty", "rate", "amount", "total", "total_amount", "total_inward", "total_outward",
           "opening_balance", "period_inward", "period_outward", "closing_balance"].includes(col.key)) {
        totals[col.key] = displayData.reduce((sum, row) => {
          const value = parseFloat(getCellValue(row, col.key)) || 0;
          return sum + value;
        }, 0);
      }
    });
    
    return totals;
  };

  const formatValue = (value, key) => {
    if (!value && value !== 0) return "-";
    if (key === "date") return new Date(value).toLocaleDateString();
    if (key === "type" || key === "category") {
      return <Tag className="stock-report-tag">{String(value).toUpperCase()}</Tag>;
    }
    if (["rate", "amount", "total_amount", "total_inward_amount", "total_outward_amount"].includes(key)) {
      return <Text className="stock-report-currency">฿{parseFloat(value).toFixed(2)}</Text>;
    }
    if (["qty", "sold_qty", "ostock", "total", "total_inward", "total_outward", "net_balance", "opening_balance", "period_inward", "period_outward", "closing_balance", "unique_items"].includes(key)) {
      return parseFloat(value).toFixed(2);
    }
    return value;
  };

  const getSortableValue = (row, column) => {
    const value = getCellValue(row, column.key);

    if (column.isCurrency || column.isNumeric) {
      return Number(value || 0);
    }

    if (column.isDate) {
      return dayjs(value).valueOf() || 0;
    }

    return String(value || "").toLowerCase();
  };

  const getCellValue = (row, key) => {
    // Handle supplier name with multiple possible field names
    if (key === "sname") {
      return row.sname || row.supplier_name || row.supplierName || "-";
    }
    if (key === "ostock") {
      return row.derived_ostock ?? row.ostock ?? "-";
    }
    if (key === "total") {
      return row.derived_closing_stock ?? row.total ?? row.closing_stock ?? "-";
    }
    if (key === "qty") {
      return row.qty ?? row.receipt_qty ?? row.in_qty ?? 0;
    }
    if (key === "sold_qty") {
      return row.sold_qty ?? row.out_qty ?? row.issue_qty ?? 0;
    }
    if (key === "pass") {
      return row.pass || row.pass_no || "-";
    }
    if (key === "ref") {
      return row.ref || row.reference || row.inv || "-";
    }
    // Handle invoice number - use only inv column from database
    if (key === "inv") {
      return row.inv || "-";
    }
    return row[key];
  };

  const getExportData = () => {
    return displayData.map((row, index) => {
      const exportRow = { ...row, sno: index + 1 };

      return columns.reduce((accumulator, column) => {
        const rawValue = column.key === "sno" ? index + 1 : getCellValue(exportRow, column.key);

        if (column.isCurrency) {
          accumulator[column.label] = Number(rawValue || 0).toFixed(2);
        } else if (column.isNumeric) {
          accumulator[column.label] = Number(rawValue || 0).toFixed(2);
        } else if (column.isDate) {
          accumulator[column.label] = rawValue ? dayjs(rawValue).format("YYYY-MM-DD") : "-";
        } else {
          accumulator[column.label] = rawValue ?? "-";
        }

        return accumulator;
      }, {});
    });
  };

  const exportTitle = `Stock Report - ${REPORT_TYPE_OPTIONS.find((option) => option.value === filterType)?.label || "All Records"}`;

  const exportFilename = `stock_report_${filterType}_${filters.from || "from"}_${filters.to || "to"}`;

  const exportHeaderInfo = {
    dayName: currentDateTime.toLocaleDateString("en-US", { weekday: "long" }),
    date: currentDateTime.toLocaleDateString("en-GB"),
    time: currentDateTime.toLocaleTimeString("en-US", { hour12: true }),
    fromDate: filters.from || "",
    toDate: filters.to || "",
    supplierName: filterType === "supplier" ? filters.sname || "All Suppliers" : ""
  };

  const addPdfHeader = (doc, title, headerInfo) => {
    let startY = 10;

    if (logo) {
      try {
        doc.addImage(logo, "PNG", 14, 8, 30, 12);
      } catch (error) {
        console.error("Failed to render logo in PDF export:", error);
      }
    }

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.text(title, 50, startY + 4);

    doc.setFontSize(8);
    doc.setFont(undefined, "normal");
    doc.text(`${headerInfo.dayName} | ${headerInfo.date} | ${headerInfo.time}`, 50, startY + 10);

    if (headerInfo.fromDate && headerInfo.toDate) {
      doc.text(`Period: ${headerInfo.fromDate} to ${headerInfo.toDate}`, 50, startY + 15);
      startY += 7;
    }

    return startY + 10;
  };

  const handleExportExcel = () => {
    const formattedData = getExportData();

    if (!formattedData.length) {
      toast.warn("No data available for Excel export");
      return;
    }

    const worksheetData = [
      [exportTitle],
      [`Period: ${filters.from || "-"} to ${filters.to || "-"}`],
      [],
      Object.keys(formattedData[0]),
      ...formattedData.map((row) => Object.values(row))
    ];

    const totals = calculateTotals();
    const exportHeaders = Object.keys(formattedData[0]);
    const amountKey = columns.find((column) => column.key === "amount" || column.key === "total")?.label;

    if (amountKey) {
      const totalRow = Array.from({ length: exportHeaders.length }, () => "");
      const amountColumnIndex = exportHeaders.indexOf(amountKey);

      totalRow[0] = "Total";
      if (amountColumnIndex >= 0) {
        totalRow[amountColumnIndex] = Number(totals.amount || totals.total || 0).toFixed(2);
      }

      worksheetData.push([]);
      worksheetData.push(totalRow);
    }

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");
    XLSX.writeFile(workbook, `${exportFilename}.xlsx`);
  };

  const handleExportPdf = () => {
    const formattedData = getExportData();

    if (!formattedData.length) {
      toast.warn("No data available for PDF export");
      return;
    }

    const doc = new jsPDF("landscape");
    const startY = addPdfHeader(doc, exportTitle, exportHeaderInfo);
    const headers = Object.keys(formattedData[0]);
    const body = formattedData.map((row) => headers.map((header) => String(row[header] ?? "-")));
    const totals = calculateTotals();
    const totalColumnIndex = headers.findIndex((header) => /amount|closing stock|total/i.test(header));

    doc.autoTable({
      startY,
      head: [headers],
      body,
      foot:
        totalColumnIndex >= 0
          ? [[
              {
                content: "TOTAL",
                colSpan: totalColumnIndex,
                styles: { halign: "right", fontStyle: "bold" }
              },
              {
                content: Number(totals.amount || totals.total || 0).toFixed(2),
                styles: { halign: "right", fontStyle: "bold" }
              },
              ...Array.from({ length: Math.max(headers.length - totalColumnIndex - 1, 0) }, () => "")
            ]]
          : undefined,
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [34, 34, 34] },
      footStyles: { fillColor: [245, 245, 245], textColor: [0, 0, 0] },
      margin: { left: 14, right: 14, bottom: 16 },
      didDrawPage: (data) => {
        const pageHeight = doc.internal.pageSize.getHeight();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`Page ${data.pageNumber}`, pageWidth / 2, pageHeight - 8, { align: "center" });
      }
    });

    doc.save(`${exportFilename}.pdf`);
  };

  const formatDateTime = () => {
    const days = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
    const dayName = days[currentDateTime.getDay()];
    const date = currentDateTime.toLocaleDateString("en-GB");
    const time = currentDateTime.toLocaleTimeString("en-US", { hour12: true });
    return { dayName, date, time };
  };

  const { dayName, date, time } = formatDateTime();

  const totals = calculateTotals();

  const summaryCards = [
    {
      title: "Records",
      value: displayData.length,
      formatter: (value) => value
    },
    {
      title: "Inward",
      value: totals.qty || totals.period_inward || totals.total_inward || 0,
      formatter: (value) => Number(value).toFixed(2)
    },
    {
      title: "Outward",
      value: totals.sold_qty || totals.period_outward || totals.total_outward || 0,
      formatter: (value) => Number(value).toFixed(2)
    },
    {
      title: "Amount",
      value: totals.amount || totals.total_amount || 0,
      formatter: (value) => `฿${Number(value).toFixed(2)}`
    }
  ];

  const tableColumns = columns.map((column, index) => ({
    title: column.label,
    dataIndex: column.key,
    key: column.key,
    width: column.key === "name" || column.key === "sname" ? 180 : undefined,
    fixed: index === 0 ? "left" : undefined,
    align: column.isNumeric || column.isCurrency ? "right" : "left",
    sorter: (leftRow, rightRow) => {
      const leftValue = getSortableValue(leftRow, column);
      const rightValue = getSortableValue(rightRow, column);

      if (typeof leftValue === "number" && typeof rightValue === "number") {
        return leftValue - rightValue;
      }

      return String(leftValue).localeCompare(String(rightValue), undefined, {
        numeric: true,
        sensitivity: "base"
      });
    },
    sortDirections: ["ascend", "descend"],
    render: (_, row, rowIndex) => {
      const value = column.key === "sno" ? rowIndex + 1 : getCellValue(row, column.key);
      return formatValue(value, column.key);
    }
  }));

  const hasFiltersApplied = Boolean(filters.from && filters.to);
  const filterSummary = [
    `Range: ${filters.from || "-"} to ${filters.to || "-"}`,
    `Report: ${REPORT_TYPE_OPTIONS.find((option) => option.value === filterType)?.label || "All Records"}`,
    filterType === "item" && filters.name ? `Item: ${filters.name}` : null,
    filterType === "supplier" && filters.sname ? `Supplier: ${filters.sname}` : null,
    filterType === "category" && filters.catid ? `Category: ${filters.catid}` : null,
    filterType === "reference" && filters.ref ? `Reference: ${filters.ref}` : null
  ].filter(Boolean);

  return (
    <Layout>
      <Header title="Stock Report" />
      <ToastContainer />
      <div className="stock-report-new">
        <Card className="stock-report-shell" bordered={false}>
          <div className="stock-report-topbar">
            <div>
              <Title level={4} className="stock-report-title">
                Minimal Stock Report
              </Title>
              <Text type="secondary">{dayName}, {date} at {time}</Text>
            </div>
            <Space wrap>
              <Button icon={<SearchOutlined />} type="primary" onClick={loadReport} loading={loading}>
                Load Report
              </Button>
              <Button icon={<ClearOutlined />} onClick={resetFilters}>
                Reset
              </Button>
              <Button
                icon={<FilePdfOutlined />}
                onClick={handleExportPdf}
                disabled={!displayData.length}
              >
                Export PDF
              </Button>
              <Button
                icon={<FileExcelOutlined />}
                onClick={handleExportExcel}
                disabled={!displayData.length}
              >
                Export Excel
              </Button>
            </Space>
          </div>

          <div className="stock-report-filter-panel">
            <Row gutter={[16, 16]} className="stock-report-filters">
              <Col xs={24} md={8} xl={6}>
                <div className="stock-report-field">
                  <Text className="stock-report-label">Report Type</Text>
                  <Select
                    value={filterType}
                    options={REPORT_TYPE_OPTIONS}
                    onChange={handleFilterTypeChange}
                    className="stock-report-control"
                  />
                </div>
              </Col>
              <Col xs={24} md={16} xl={10}>
                <div className="stock-report-field">
                  <Text className="stock-report-label">Date Range</Text>
                  <RangePicker
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    format="YYYY-MM-DD"
                    className="stock-report-control"
                    placeholder={["Start Date", "End Date"]}
                  />
                </div>
              </Col>
              {filterType === "item" && (
                <Col xs={24} md={12} xl={8}>
                  <div className="stock-report-field">
                    <Text className="stock-report-label">Item</Text>
                    <Select
                      value={filters.name || undefined}
                      options={filterOptions.items.map((item) => ({ value: item.name, label: item.label }))}
                      onChange={(value) => handleSelectFilterChange("name", value)}
                      allowClear
                      showSearch
                      className="stock-report-control"
                      optionFilterProp="label"
                      placeholder="Select item"
                    />
                  </div>
                </Col>
              )}
              {filterType === "supplier" && (
                <Col xs={24} md={12} xl={8}>
                  <div className="stock-report-field">
                    <Text className="stock-report-label">Supplier</Text>
                    <Select
                      value={filters.sname || undefined}
                      options={filterOptions.suppliers}
                      onChange={(value) => handleSelectFilterChange("sname", value)}
                      allowClear
                      showSearch
                      className="stock-report-control"
                      optionFilterProp="label"
                      placeholder="Select supplier"
                    />
                  </div>
                </Col>
              )}
              {filterType === "category" && (
                <Col xs={24} md={12} xl={8}>
                  <div className="stock-report-field">
                    <Text className="stock-report-label">Category</Text>
                    <Select
                      value={filters.catid || undefined}
                      options={filterOptions.categories}
                      onChange={(value) => handleSelectFilterChange("catid", value)}
                      allowClear
                      className="stock-report-control"
                      placeholder="Select category"
                    />
                  </div>
                </Col>
              )}
              {filterType === "reference" && (
                <Col xs={24} md={12} xl={8}>
                  <div className="stock-report-field">
                    <Text className="stock-report-label">Reference</Text>
                    <Input
                      name="ref"
                      value={filters.ref}
                      onChange={handleFilterChange}
                      className="stock-report-control"
                      placeholder="Search reference"
                    />
                  </div>
                </Col>
              )}
            </Row>

            {hasFiltersApplied && (
              <Alert
                className="stock-report-alert"
                type="info"
                showIcon
                message={filterSummary.join(" • ")}
              />
            )}
          </div>

          <Row gutter={[16, 16]} className="stock-report-stats">
            {summaryCards.map((card) => (
              <Col xs={12} lg={6} key={card.title}>
                <Card bordered={false} className="stock-report-stat-card">
                  <Statistic title={card.title} value={card.value} formatter={card.formatter} />
                </Card>
              </Col>
            ))}
          </Row>

          <Card bordered={false} className="stock-report-table-card">
            {loading ? (
              <div className="stock-report-loading">
                <Spin size="large" />
              </div>
            ) : !displayData.length ? (
              <Empty
                description="Select filters and load the report"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <Table
                rowKey={(record, index) => record.id || `${record.po || "row"}-${index}`}
                columns={tableColumns}
                dataSource={displayData}
                pagination={
                  pagination.total_pages > 1
                    ? {
                        current: pagination.current_page,
                        pageSize: pagination.limit,
                        total: pagination.total_records,
                        onChange: handlePageChange,
                        showSizeChanger: false,
                        showTotal: (total) => `${total} records`
                      }
                    : false
                }
                scroll={{ x: "max-content" }}
                summary={() => (
                  <Table.Summary.Row>
                    {columns.map((column, index) => {
                      const totalValue = totals[column.key];

                      if (index === 0) {
                        return (
                          <Table.Summary.Cell index={index} key={column.key}>
                            <Text strong>Total</Text>
                          </Table.Summary.Cell>
                        );
                      }

                      if (typeof totalValue === "undefined") {
                        return <Table.Summary.Cell index={index} key={column.key}>-</Table.Summary.Cell>;
                      }

                      return (
                        <Table.Summary.Cell index={index} key={column.key} align="right">
                          <Text strong>
                            {column.isCurrency
                              ? `฿${Number(totalValue).toFixed(2)}`
                              : Number(totalValue).toFixed(2)}
                          </Text>
                        </Table.Summary.Cell>
                      );
                    })}
                  </Table.Summary.Row>
                )}
              />
            )}
          </Card>
        </Card>
      </div>
    </Layout>
  );
}
